import { useMemo, useState, type ReactNode } from "react";

import type { SpecType } from "@/api/contracts/specifications";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import {
  NestedSortableList,
  type SortableEntry,
  type SortableGroup,
} from "@/components/ui/sortable-rows";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import { useMoveSpecGroup } from "@/hooks/spec-editor-groups";
import {
  useSpecScrollRegion,
  type SpecGroupExpansion,
  type SpecLinesLoader,
} from "@/hooks/spec-lines";
import type { SpecLinesGroup, SpecLinesLine } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";

import { EditableSpecGroup } from "./editable-spec-group";
import type { SpecGroupMemory } from "./spec-group";

// The Editor's groups — the donor's `DragDropContext` round its groups grid
// (`SpecEditIsland.tsx:200-237,299-304`) — as a drop-in for #806's page frame
// (#805).
//
//   - **The groups** of the loader's ordering, up to the display cursor and no
//     further (`index > groupDisplayCursor → null`): a group not rendered is
//     not a drop target, and nothing lands below the last rendered one.
//   - **One drag context, two levels** (`NestedSortableList`). A group drop
//     moves through #799's move hook with `afterId` = its visible predecessor,
//     `null` when first (structure rulings R2): the hook's guess reorders at
//     once, and on a failure its harness puts every group back.
//   - **The line level** is ready for #809: each group's lines drawn by the
//     caller's `renderLine`, inside the group's own drop list, and — once the
//     caller passes `onMoveLine` — draggable within that group only, the drop
//     handed over with the line, its group and its visible predecessor. A
//     rejected line move shows here too.
//   - **One drag-error snackbar for both levels**, as the donor's single
//     `errorDnd` serves both (`data/actions.ts:48-52,105,156`).
//   - **The donor's edge auto-scroll** (#869 D4 a): rbd's fluid scroller, the
//     window first and then the Editor's pane — `Layout.tsx`'s grid, the
//     droppables' scroll container from `lg` (`SpecScrollRegionContext`).

/** A line drop: the line, its group, and the line it now follows (`null` first). */
export interface SpecLineMove {
  lineId: number;
  groupId: number;
  afterId: number | null;
}

export interface EditableSpecGroupsProps {
  loader: Pick<SpecLinesLoader, "ordering" | "displayCursor">;
  expansion: SpecGroupExpansion;
  target: SpecEditorTarget;
  /** `Add specification line` on a group's card. */
  onAddLine: (group: SpecLinesGroup) => void;
  /** A line's row (#809); without it the groups draw no rows. */
  renderLine?: (line: SpecLinesLine, group: SpecLinesGroup) => ReactNode;
  /** A line drop (#809). A rejection's message shows on the drag snackbar. */
  onMoveLine?: (move: SpecLineMove) => Promise<unknown>;
  /** The spec's type: each flagged group header's sentence and tint (#1104). */
  specType?: SpecType;
  /** "Show changes from Base": the group flag's header (#1104). */
  showChanges?: boolean;
}

interface GroupEntry extends SortableGroup<LineEntry> {
  group: SpecLinesGroup;
}

interface LineEntry extends SortableEntry {
  line: SpecLinesLine;
}

const idOf = (id: string | null) => (id === null ? null : Number(id));

const EDITOR_ERRORS: ReadonlySet<string> = new Set(Object.values(ERRORS));

/** An error's message when it is one of the Editor's own (#797 `ERRORS`, chain
 *  decision D1), else `fallback` — no other words reach the snackbar. */
const editorErrorOr = (error: unknown, fallback: string) =>
  error instanceof Error && EDITOR_ERRORS.has(error.message) ? error.message : fallback;

export function EditableSpecGroups({
  loader,
  expansion,
  target,
  onAddLine,
  renderLine,
  onMoveLine,
  specType,
  showChanges = false,
}: EditableSpecGroupsProps) {
  const move = useMoveSpecGroup();
  const scrollRegion = useSpecScrollRegion();
  const [dragError, setDragError] = useState<string | null>(null);
  // Each card's fold and note as they stand, for the copy a lift draws.
  const [memory, setMemory] = useState<Record<number, Partial<SpecGroupMemory>>>({});
  const remember = (groupId: number) => (patch: Partial<SpecGroupMemory>) =>
    setMemory((current) => {
      const held = current[groupId] ?? {};
      const same = (Object.keys(patch) as (keyof SpecGroupMemory)[]).every(
        (key) => held[key] === patch[key],
      );
      return same ? current : { ...current, [groupId]: { ...held, ...patch } };
    });

  const groups = useMemo<GroupEntry[]>(
    () =>
      (loader.ordering ?? [])
        .filter((_, index) => index <= loader.displayCursor)
        .map(({ group, lines }) => ({
          id: String(group.id),
          label: group.name,
          group,
          lines: lines.map((line) => ({ id: String(line.id), label: line.name, line })),
        })),
    [loader.ordering, loader.displayCursor],
  );

  return (
    <div data-testid="groups-grid">
      <NestedSortableList<LineEntry, GroupEntry>
        aria-label="Specification groups"
        groups={groups}
        donorAutoScroll={{ region: scrollRegion }}
        renderGroup={({ group }, { lines, lifted }) => (
          <EditableSpecGroup
            group={group}
            expansion={expansion}
            target={target}
            lines={lines}
            lifted={lifted}
            onAddLine={() => onAddLine(group)}
            memory={lifted ? { seed: memory[group.id] } : { onChange: remember(group.id) }}
            specType={specType}
            showChanges={showChanges}
          />
        )}
        renderLine={renderLine ? ({ line }, { group }) => renderLine(line, group) : undefined}
        onMoveGroup={({ id, afterId }) => {
          move
            .mutateAsync({ ...target(), groupId: Number(id), afterId: idOf(afterId) })
            .catch(() => setDragError(ERRORS.MOVE_GROUP));
        }}
        onMoveLine={
          onMoveLine
            ? ({ id, groupId, afterId }) => {
                onMoveLine({ lineId: Number(id), groupId: Number(groupId), afterId: idOf(afterId) })
                  // The line verb's own `ERRORS` string, from its harness;
                  // `MOVE_LINE` for anything that is not one of them.
                  .catch((error: unknown) => setDragError(editorErrorOr(error, ERRORS.MOVE_LINE)));
              }
            : undefined
        }
      />
      <ErrorSnackbar
        open={dragError !== null}
        message={dragError ?? ""}
        onClose={() => setDragError(null)}
      />
    </div>
  );
}
